/**
 * CallManager — Phase 8 skeleton.
 *
 * Public API for placing, accepting, declining, and ending calls. Owns:
 *   - CallSession state machines in memory (never persisted)
 *   - call_history rows (persisted on terminal transitions)
 *   - audit_log entries per state transition
 *
 * Stubs the actual WebRTC + libp2p signaling — those edges throw
 * `NotImplementedInBrowser` for now so consumers can author against the
 * stable API while the browser-only media path lands incrementally.
 *
 * Per the build plan in docs/voice-video-design.md, this file is
 * concretely steps 1 + 10 (state machine + call_history). Steps 2-9
 * (libp2p protocol, SDP/ICE exchange, WebRTC media, ICE restart, group
 * mesh, etc.) wire in here as drop-in implementations replacing the
 * `_signalRing`, `_openSignalingStream`, etc. internal methods.
 */

import { v4 as uuid } from 'uuid';

import type { AppContext } from '../app';
import { signEnvelope } from '../envelope/Envelope';
import type {
  AuditLogRecord,
  CallHistoryRecord,
  CallDisposition,
} from '../storage/schema';
import {
  CallSession,
  type CallContext as CallSessionContext,
  type CallEvent,
} from './CallSession';
import {
  CallProtocol,
  type CallSignalingMessage,
  type CallSignalingTransport,
} from './CallProtocol';
import type { CallController } from './CallController';

export type CallKind = 'audio' | 'video';

/**
 * Factory callable by CallManager when it wants a CallController for a
 * given session. Lets the UI (which owns WebRtcMediaEngine + TURN config)
 * decide HOW to build the controller without CallManager taking a hard
 * dep on the browser-only media stack.
 */
export type ControllerFactory = (opts: {
  session: CallSession;
  remotePeerId: string;
  protocol: CallProtocol;
}) => CallController;

export interface RingOptions {
  threadId: string;
  /** Pubkey of the peer we're calling (or the caller, when constructing an incoming session). */
  peerPubkey: string;
  kind: CallKind;
}

export interface RingResult {
  callId: string;
  /** The session ID — same as callId in v1. Surface UIs use this to bind to the session. */
  sessionId: string;
}

/**
 * Thrown for any API surface that requires a real browser + libp2p stack.
 * The CallManager skeleton lands in v1 of Phase 8; subsequent build-plan
 * steps replace these throws with real implementations.
 */
export class NotImplementedInBrowser extends Error {
  constructor(method: string) {
    super(`${method}: requires browser WebRTC + libp2p signaling (Phase 8 build-plan steps 2-9)`);
    this.name = 'NotImplementedInBrowser';
  }
}

export class CallManager {
  readonly #ctx: AppContext;
  /** Active sessions in memory, keyed by callId. */
  readonly #sessions = new Map<string, CallSession>();
  /**
   * Phase 19.3: live CallController per active callId. Populated by ring/
   * accept when a controller factory is registered. Renderers read this
   * via `getController(callId)` to subscribe to remote-track + state
   * events and wire video elements.
   */
  readonly #controllers = new Map<string, CallController>();
  /** Optional protocol; when set, _signalRing/_openSignalingStream use it. */
  #protocol: CallProtocol | null = null;
  /** Phase 19.3: factory for instantiating per-call controllers. UI sets this once at boot. */
  #controllerFactory: ControllerFactory | null = null;

  constructor(ctx: AppContext, opts: { transport?: CallSignalingTransport } = {}) {
    this.#ctx = ctx;
    if (opts.transport !== undefined) {
      this.#protocol = new CallProtocol(opts.transport);
      this.#protocol.onSignal(async (peer, msg) => {
        await this.#handleInboundSignal(peer, msg);
      });
    }
  }

  /** Phase 19.3: register the controller factory. Called once at app boot. */
  setControllerFactory(factory: ControllerFactory | null): void {
    this.#controllerFactory = factory;
  }

  /** Phase 19.3: look up the live controller for a call, if any. */
  getController(callId: string): CallController | undefined {
    return this.#controllers.get(callId);
  }

  /** Phase 19.3: enumerate all live controllers. */
  liveControllers(): CallController[] {
    return [...this.#controllers.values()];
  }

  /**
   * Phase 19.3: instantiate + register a CallController for a session, if
   * a factory is set + protocol is configured. Returns the controller or
   * undefined when not configured. Safe to call multiple times — returns
   * the existing controller on repeat calls.
   */
  #ensureController(session: CallSession, peerId: string): CallController | undefined {
    const existing = this.#controllers.get(session.context.callId);
    if (existing !== undefined) return existing;
    if (this.#controllerFactory === null || this.#protocol === null) return undefined;
    const controller = this.#controllerFactory({
      session,
      remotePeerId: peerId,
      protocol: this.#protocol,
    });
    this.#controllers.set(session.context.callId, controller);
    return controller;
  }

  /** Bring up the call protocol (registers libp2p handler or in-mem bus). */
  async startProtocol(): Promise<void> {
    if (this.#protocol === null) {
      throw new NotImplementedInBrowser('CallManager.startProtocol: no transport configured');
    }
    await this.#protocol.start();
  }

  /** Tear down the protocol — flushes outstanding streams. */
  async stopProtocol(): Promise<void> {
    if (this.#protocol === null) return;
    await this.#protocol.stop();
  }

  /** Returns the live in-memory session if one exists. */
  getSession(callId: string): CallSession | undefined {
    return this.#sessions.get(callId);
  }

  /** Convenience: all live sessions. */
  liveSessions(): CallSession[] {
    return [...this.#sessions.values()];
  }

  /**
   * Place an outgoing ring. Allocates a callId, creates a CallSession in
   * `ringing` state, audits, then attempts to deliver the ring envelope.
   * The envelope delivery is the v1 stubbed edge — for now we just queue
   * the audit and let the manager drive the state machine via apply().
   */
  async ring(opts: RingOptions): Promise<RingResult> {
    const callId = uuid();
    const startedAt = new Date().toISOString();
    const ctx: CallSessionContext = {
      callId,
      threadId: opts.threadId,
      direction: 'outgoing',
      kind: opts.kind,
      peerPubkey: opts.peerPubkey,
      startedAt,
      reconnectAttempts: 0,
    };
    const session = new CallSession(ctx);
    const r = session.apply('ring');
    if (!r.ok) {
      throw new Error(`CallManager.ring: state machine rejected: ${r.reason}`);
    }
    this.#sessions.set(callId, session);
    await this.#auditTransition(session, 'call.ringing');

    // Persist an in-progress row so the UI can show it in call_history immediately.
    await this.#writeHistory(session, 'in-progress');

    // Phase 58.b (v11): publish the ring envelope to the thread's sync stream
    // so the callee's ThreadInbox can dispatch it into their own CallManager
    // and spawn the call-incoming surface. Best-effort — if the stream
    // append fails (no thread / no sync) the ring still proceeds and the
    // push-relay wakeup path remains the fallback for backgrounded callees.
    void (async () => {
      try { await this._signalRing(callId); }
      catch (err) {
        // eslint-disable-next-line no-console
        console.warn(`CallManager.ring: _signalRing failed: ${err instanceof Error ? err.message : String(err)}`);
      }
    })();

    // Phase 19.3: instantiate a CallController if the UI has registered a
    // factory. Kicks off the actual WebRTC offer/answer once placeCall()
    // is called by the caller side.
    this.#ensureController(session, opts.peerPubkey);

    // Phase 27.8: wake the callee via the operator's push relay. Critical
    // for mobile callees whose ALDS PWA is backgrounded. Fire-and-forget;
    // failure never affects the ring path.
    void (async () => {
      try {
        const { getWakeupNotifier } = await import('../credentials/WakeupNotifier');
        await getWakeupNotifier(this.#ctx).wake({
          targetPubkey: opts.peerPubkey,
          kind: 'alds-call-ringing',
          details: { callId, kind: opts.kind, threadId: opts.threadId },
        });
      } catch { /* swallow */ }
    })();

    return { callId, sessionId: callId };
  }

  /**
   * Register an incoming ring (called by the inbox when a ring envelope
   * lands on a thread we're a member of).
   */
  async registerIncoming(args: {
    callId: string;
    threadId: string;
    callerPubkey: string;
    kind: CallKind;
  }): Promise<CallSession> {
    if (this.#sessions.has(args.callId)) {
      // Already known (re-replay) — return existing.
      return this.#sessions.get(args.callId)!;
    }
    const startedAt = new Date().toISOString();
    const session = new CallSession({
      callId: args.callId,
      threadId: args.threadId,
      direction: 'incoming',
      kind: args.kind,
      peerPubkey: args.callerPubkey,
      startedAt,
      reconnectAttempts: 0,
    });
    session.apply('ring');
    this.#sessions.set(args.callId, session);
    await this.#auditTransition(session, 'call.ringing');
    await this.#writeHistory(session, 'in-progress');
    // Phase 19.3: prepare a controller for incoming calls too. The UI's
    // accept flow will drive offer/answer via the controller.
    this.#ensureController(session, args.callerPubkey);
    return session;
  }

  /** Local user accepts an incoming ring. */
  async accept(callId: string): Promise<void> {
    const session = this.#requireSession(callId);
    const r = session.apply('accept');
    if (!r.ok) throw new Error(`CallManager.accept: ${r.reason}`);
    session.setAcceptedAt(new Date().toISOString());
    await this.#auditTransition(session, 'call.accepted');
    // Build-plan step 3/4 — open signaling stream + start WebRTC. Stubbed.
    // Phase 19.3: ensure controller exists in case the inbox bypass didn't run.
    this.#ensureController(session, session.context.peerPubkey);
  }

  /** Local user declines an incoming ring. */
  async decline(callId: string): Promise<void> {
    const session = this.#requireSession(callId);
    const r = session.apply('decline');
    if (!r.ok) throw new Error(`CallManager.decline: ${r.reason}`);
    await this.#finalize(session, 'call.declined');
  }

  /** Cancel an outgoing ring before remote accepts. */
  async cancel(callId: string): Promise<void> {
    const session = this.#requireSession(callId);
    const r = session.apply('cancel');
    if (!r.ok) throw new Error(`CallManager.cancel: ${r.reason}`);
    await this.#finalize(session, 'call.cancelled');
  }

  /** Local user ends a connected (or connecting) call. */
  async endCall(callId: string): Promise<void> {
    const session = this.#requireSession(callId);
    const stateBefore = session.state;
    const r = session.apply('end');
    if (!r.ok) throw new Error(`CallManager.endCall: ${r.reason}`);
    await this.#auditTransition(session, 'call.ending');
    // In production: send bye envelope, await ack. Stubbed → immediately
    // dispatch bye-sent.
    void stateBefore;
    const r2 = session.apply('bye-sent');
    if (!r2.ok) {
      // Should never happen given the transition table; surface for debug.
      throw new Error(`CallManager.endCall: bye-sent rejected: ${r2.reason}`);
    }
    await this.#finalize(session, 'call.ended');
  }

  /**
   * Mark a ring timeout (incoming or outgoing). Caller (typically a
   * setTimeout in the UI or a heartbeat) decides when this fires.
   */
  async timeout(callId: string): Promise<void> {
    const session = this.#requireSession(callId);
    const r = session.apply('timeout');
    if (!r.ok) throw new Error(`CallManager.timeout: ${r.reason}`);
    await this.#finalize(session, 'call.timed-out');
  }

  /** Apply an arbitrary event — used by inbox + WebRTC listeners as they wire in. */
  async fireEvent(callId: string, event: CallEvent): Promise<void> {
    const session = this.#requireSession(callId);
    const r = session.apply(event);
    if (!r.ok) throw new Error(`CallManager.fireEvent(${event}): ${r.reason}`);
    if (session.isTerminal()) {
      await this.#finalize(session, `call.${event}`);
    } else {
      await this.#auditTransition(session, `call.${event}`);
    }
  }

  /** Read call history rows (most-recent first). */
  async listCallHistory(opts: { threadId?: string; limit?: number } = {}): Promise<CallHistoryRecord[]> {
    const db = this.#ctx.storage.db;
    const limit = opts.limit ?? 50;
    let rows: CallHistoryRecord[];
    if (opts.threadId !== undefined) {
      rows = await db.call_history.where('threadId').equals(opts.threadId).toArray();
    } else {
      rows = await db.call_history.toArray();
    }
    rows.sort((a, b) => b.startedAt.localeCompare(a.startedAt));
    return rows.slice(0, limit);
  }

  // ─── PHASE 8 STEPS 2-9: STUBBED BROWSER-ONLY EDGES ──────────────────────
  //
  // These methods are the seams where libp2p signaling + WebRTC come in.
  // They throw in v1 of Phase 8; callers should not invoke them yet.
  // Replace these stubs incrementally as the build plan progresses.

  // ─── BUILD-PLAN STEPS 2 + 3 — now implemented ──────────────────────────

  /**
   * Build-plan step 2 — register the libp2p call protocol via the
   * configured transport. Equivalent to calling `startProtocol()`; kept
   * as a stable name for older callers.
   */
  async _registerProtocol(): Promise<void> {
    return this.startProtocol();
  }

  /**
   * Build-plan step 3 — send the ring envelope via MLS to the incident's
   * thread. The envelope kind is 'call-ring' with the caller's peerId and
   * the callId. The callee's ThreadInbox picks it up and forwards into
   * the CallManager (registerIncoming).
   */
  async _signalRing(callId: string): Promise<{ envelopeHash: string }> {
    const session = this.#requireSession(callId);
    const c = session.context;
    const payload = JSON.stringify({
      kind: 'call-ring',
      callId,
      callerPeerId: this.#ctx.meshNode.peerId,
      callerPubkey: c.peerPubkey,
      callKind: c.kind,
      ringedAt: new Date().toISOString(),
    });
    const env = await signEnvelope({
      identity: this.#ctx.identity,
      context: { environment: 'chat', purpose: `thread-${c.threadId}` },
      contentType: 'application/json; profile=call-ring',
      payload,
      lamport: 0,
      parentHash: '',
    });
    try {
      await this.#ctx.sync.append(`mesh-thread-${c.threadId}`, env);
    } catch (err) {
      // Ring delivery is best-effort at the sync layer — record but don't
      // abort the call setup. The callee may pick up later via OrbitDB
      // replication when the ring envelope lands.
      // eslint-disable-next-line no-console
      console.warn(`CallManager._signalRing: sync.append failed: ${err instanceof Error ? err.message : String(err)}`);
    }
    return { envelopeHash: env.hash };
  }

  /**
   * Build-plan step 3 — open a libp2p stream to the callee for SDP/ICE
   * exchange. Returns immediately once the protocol stream is established;
   * subsequent SDP/ICE messages flow via `sendOffer` / `sendAnswer` /
   * `sendIce`. WebRTC media setup is build-plan step 4 (browser-only).
   */
  async _openSignalingStream(callId: string): Promise<void> {
    if (this.#protocol === null) {
      throw new NotImplementedInBrowser('CallManager._openSignalingStream: no transport configured');
    }
    // Touch the session to confirm it exists; the actual stream is set up
    // lazily by the transport on first send.
    this.#requireSession(callId);
    // No-op at this layer — the InMemoryCallTransport routes per-message;
    // the real Libp2pCallTransport opens on first send (build-plan step 3.1).
  }

  /** Send an SDP offer via the call protocol. */
  async sendOffer(callId: string, peerId: string, sdp: string): Promise<void> {
    if (this.#protocol === null) {
      throw new NotImplementedInBrowser('CallManager.sendOffer: no transport configured');
    }
    await this.#protocol.sendOffer(peerId, callId, sdp);
  }

  /** Send an SDP answer. */
  async sendAnswer(callId: string, peerId: string, sdp: string): Promise<void> {
    if (this.#protocol === null) {
      throw new NotImplementedInBrowser('CallManager.sendAnswer: no transport configured');
    }
    await this.#protocol.sendAnswer(peerId, callId, sdp);
  }

  /** Send an ICE candidate. */
  async sendIce(callId: string, peerId: string, candidate: string): Promise<void> {
    if (this.#protocol === null) {
      throw new NotImplementedInBrowser('CallManager.sendIce: no transport configured');
    }
    await this.#protocol.sendIce(peerId, callId, candidate);
  }

  // ─── inbound signaling dispatcher ────────────────────────────────────────

  async #handleInboundSignal(peer: string, msg: CallSignalingMessage): Promise<void> {
    const session = this.#sessions.get(msg.callId);
    if (session === undefined) {
      // Could be a fresh inbound; in that case the ring envelope through
      // the thread should have set up the session via registerIncoming.
      // If neither, drop with audit.
      await this.#auditOrphanSignal(peer, msg);
      return;
    }
    switch (msg.kind) {
      case 'sdp-offer':
      case 'sdp-answer':
        // Receiving an SDP indicates negotiation moves forward. The
        // CallSession transitions on 'media-flowing' once tracks attach;
        // until then we're in 'connecting'. No state change here — the
        // browser RTCPeerConnection drives the lifecycle.
        break;
      case 'ice':
        // ICE candidates: pass to RTCPeerConnection in browser code.
        break;
      case 'bye':
        if (!session.isTerminal()) {
          try { await this.endCall(msg.callId); } catch { /* ignore */ }
        }
        break;
    }
  }

  async #auditOrphanSignal(peer: string, msg: CallSignalingMessage): Promise<void> {
    const occurredAt = new Date().toISOString();
    const record: AuditLogRecord = {
      seq: undefined as unknown as number,
      category: 'call',
      action: 'call.orphan-signal',
      context: 'chat:call',
      details: { peer, kind: msg.kind, callId: msg.callId },
      occurredAt,
      signature: new Uint8Array(0),  // un-signed; orphan signals are diagnostic only
    };
    try { await this.#ctx.storage.db.audit_log.add(record); }
    catch { /* swallow */ }
  }

  // ─── internals ──────────────────────────────────────────────────────────

  #requireSession(callId: string): CallSession {
    const s = this.#sessions.get(callId);
    if (s === undefined) throw new Error(`CallManager: unknown callId ${callId}`);
    return s;
  }

  async #finalize(session: CallSession, action: string): Promise<void> {
    if (!session.isTerminal()) {
      throw new Error(`CallManager.#finalize: session ${session.context.callId} not terminal (${session.state})`);
    }
    const endedAt = new Date().toISOString();
    session.setEndedAt(endedAt);
    const disposition = session.toDisposition();
    await this.#writeHistory(session, disposition);
    await this.#auditTransition(session, action);
    // Drop from in-memory map — terminal sessions don't need to be live.
    this.#sessions.delete(session.context.callId);
    // Phase 19.3: tear down the controller too, in case it's still alive.
    const controller = this.#controllers.get(session.context.callId);
    if (controller !== undefined) {
      this.#controllers.delete(session.context.callId);
      try { await controller.end('finalized'); } catch { /* best-effort */ }
    }
  }

  async #writeHistory(session: CallSession, disposition: CallDisposition): Promise<void> {
    const c = session.context;
    const db = this.#ctx.storage.db;
    const startedMs = Date.parse(c.startedAt);
    const endedMs = c.endedAt ? Date.parse(c.endedAt) : null;
    const record: CallHistoryRecord = {
      callId: c.callId,
      context: `chat:thread-${c.threadId}`,
      threadId: c.threadId,
      participantPubkeys: [c.peerPubkey],
      direction: c.direction,
      disposition,
      startedAt: c.startedAt,
      endedAt: c.endedAt ?? null,
      mediaTypes: [c.kind === 'video' ? 'video' : 'audio'],
      ...(endedMs !== null
        ? { durationSeconds: Math.max(0, Math.round((endedMs - startedMs) / 1000)) }
        : {}),
    };
    await db.call_history.put(record);
  }

  async #auditTransition(session: CallSession, action: string): Promise<void> {
    const c = session.context;
    const occurredAt = new Date().toISOString();
    const last = session.history[session.history.length - 1];
    const canonical = JSON.stringify({
      category: 'call' as const,
      action,
      callId: c.callId,
      from: last?.from,
      to: last?.to,
      occurredAt,
    });
    const signature = await this.#ctx.identity.sign(
      new TextEncoder().encode(canonical),
      { environment: 'chat', purpose: `thread-${c.threadId}` },
    );
    const record: AuditLogRecord = {
      seq: undefined as unknown as number,
      category: 'call',
      action,
      context: `chat:thread-${c.threadId}`,
      details: {
        callId: c.callId,
        threadId: c.threadId,
        direction: c.direction,
        kind: c.kind,
        peerPubkey: c.peerPubkey,
        ...(last ? { from: last.from, to: last.to, via: last.via } : {}),
        state: session.state,
      },
      occurredAt,
      signature,
    };
    try { await this.#ctx.storage.db.audit_log.add(record); }
    catch (err) {
      // eslint-disable-next-line no-console
      console.warn(`CallManager.audit: ${err instanceof Error ? err.message : String(err)}`);
    }
  }
}

// ─── Phase 19.3: per-AppContext singleton ───────────────────────────────────
//
// Surfaces share a single CallManager so the controller registry stays
// consistent across the React tree. Keyed by AppContext via WeakMap so the
// manager is garbage-collected when the context tears down.

const MANAGER_BY_CTX = new WeakMap<AppContext, CallManager>();

export function getCallManager(ctx: AppContext): CallManager {
  let mgr = MANAGER_BY_CTX.get(ctx);
  if (mgr === undefined) {
    mgr = new CallManager(ctx);
    MANAGER_BY_CTX.set(ctx, mgr);
  }
  return mgr;
}
